import React from "react";
import { Sparkles, Mail, ArrowUpRight, Infinity as InfinityIcon } from "lucide-react";
import { usePlan } from "@/lib/plan";

const FOUNDER_EMAIL = "intel@therevenuecycleiq.com";

const PLAN_COPY = {
  starter: {
    label: "Starter",
    accent: "#64748B",
    upgradeTo: "Professional",
  },
  professional: {
    label: "Professional",
    accent: "#0F766E",
    upgradeTo: "Enterprise",
  },
  enterprise: {
    label: "Enterprise",
    accent: "#7C3AED",
    upgradeTo: null,
  },
};

function UsageBar({ label, used, cap, pct, testId }) {
  const isUnlimited = cap == null;
  const percent = isUnlimited ? 0 : Math.min(100, pct ?? 0);
  const overage = !isUnlimited && used > cap;
  const barColor = overage ? "#EF4444" : percent >= 80 ? "#F59E0B" : "#14B8A6";

  return (
    <div className="flex-1 min-w-[180px]" data-testid={testId}>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">{label}</span>
        <span className="text-[11px] tabular-nums font-semibold text-slate-200">
          {used.toLocaleString()}
          <span className="text-slate-500"> / {isUnlimited ? <InfinityIcon className="inline h-3 w-3" /> : cap.toLocaleString()}</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: isUnlimited ? "0%" : `${percent}%`, background: barColor }}
        />
      </div>
      {overage ? (
        <div className="text-[10px] text-red-400 mt-1">Over cap — upgrade to avoid future limits</div>
      ) : percent >= 80 ? (
        <div className="text-[10px] text-amber-400 mt-1">{Math.round(percent)}% of monthly cap</div>
      ) : null}
    </div>
  );
}

export function PlanUsageWidget() {
  const { plan, plan_name: planName, loaded, capabilities, limits, usage } = usePlan();

  // Enterprise customers have no caps to display — skip entirely.
  if (!loaded || plan === "enterprise") return null;

  const copy = PLAN_COPY[plan] || PLAN_COPY.starter;
  const showUpgrade = Boolean(copy.upgradeTo);

  const subject = encodeURIComponent(`Upgrade request — Revenue Cycle IQ`);
  const body = encodeURIComponent(
    `Hi team,\n\nWe'd like to upgrade our Revenue Cycle IQ AI plan from ${planName} to ${copy.upgradeTo}.\n\nThanks!`
  );
  const mailto = `mailto:${FOUNDER_EMAIL}?subject=${subject}&body=${body}`;

  return (
    <div
      data-testid="plan-usage-widget"
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#0A192F] via-[#0F2238] to-[#0A192F] px-5 py-4 sm:px-6 sm:py-5"
    >
      <div className="absolute -top-12 -right-8 h-40 w-40 rounded-full bg-teal-400/10 blur-3xl pointer-events-none" />
      <div className="relative flex flex-col lg:flex-row lg:items-center gap-5">
        {/* Plan identity */}
        <div className="flex items-center gap-3 lg:min-w-[200px]">
          <div
            className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${copy.accent}33`, border: `1px solid ${copy.accent}66` }}
          >
            <Sparkles className="h-4 w-4" style={{ color: copy.accent }} />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Current Plan</div>
            <div className="font-display text-lg text-white font-semibold leading-tight" data-testid="plan-usage-widget-plan-name">
              {planName}
            </div>
          </div>
        </div>

        {/* Usage bars */}
        <div className="flex flex-col sm:flex-row flex-1 gap-4 sm:gap-6">
          <UsageBar
            label="Denials this month"
            used={usage?.denials_uploaded ?? 0}
            cap={limits?.monthly_denial_cap}
            pct={usage?.denials_pct}
            testId="plan-usage-widget-denials"
          />
          {!capabilities?.appeals_unlimited ? (
            <UsageBar
              label="Appeals this month"
              used={usage?.appeals_generated ?? 0}
              cap={limits?.monthly_appeal_cap}
              pct={usage?.appeals_pct}
              testId="plan-usage-widget-appeals"
            />
          ) : null}
        </div>

        {/* Upgrade CTA */}
        {showUpgrade ? (
          <a
            href={mailto}
            data-testid="plan-usage-widget-upgrade"
            className="inline-flex items-center gap-1.5 rounded-lg bg-teal-500 hover:bg-teal-400 text-slate-900 px-4 py-2.5 text-sm font-bold transition-colors shadow-lg shadow-teal-500/20 flex-shrink-0"
            title={`Upgrade to ${copy.upgradeTo}`}
          >
            <Mail className="h-3.5 w-3.5" />
            Upgrade to {copy.upgradeTo}
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        ) : null}
      </div>
    </div>
  );
}
